'use client';

/**
 * 99 NOMS D'ALLAH (Asma-ul-Husna)
 * Grille des 99 noms (arabe vocalisé + translittération + sens FR/EN),
 * détail avec explication + référence coranique, TTS arabe,
 * mémorisation + favoris persistés (localStorage), progression x/99.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import { speakText, stopSpeech } from '@/lib/speech';
import PageBanner from './PageBanner';
import { Volume2, Heart, CheckCircle2, Circle, Search, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import names99 from '@/data/names99.json';

interface NameEntry { n: number; ar: string; tr: string; fr: string; en: string; exp: string; ref: string }

const NAMES = names99 as NameEntry[];
const STORE_KEY = 'q1000_names99';

interface NameState { memorized: Record<number, boolean>; favs: Record<number, boolean> }

function loadState(): NameState {
  if (typeof window === 'undefined') return { memorized: {}, favs: {} };
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{"memorized":{},"favs":{}}'); } catch { return { memorized: {}, favs: {} }; }
}

export default function NamesSection() {
  const { uiLang } = useApp();
  const t = DICTS[uiLang];
  const [state, setState] = useState<NameState>({ memorized: {}, favs: {} });
  const [selected, setSelected] = useState<NameEntry | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'memorized' | 'favs'>('all');
  const [hydrated, setHydrated] = useState(false);

  // Hydratation locale (ajustement d'état pendant le rendu)
  if (!hydrated) {
    setHydrated(true);
    setState(loadState());
  }

  // Échap ferme la modale détail
  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const persist = (next: NameState) => {
    setState(next);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const lbl = (fr: string, en: string, ar: string) => (uiLang === 'ar' ? ar : uiLang === 'en' ? en : fr);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return NAMES.filter((n) => {
      if (filter === 'memorized' && !state.memorized[n.n]) return false;
      if (filter === 'favs' && !state.favs[n.n]) return false;
      if (!q) return true;
      return n.tr.toLowerCase().includes(q) || n.fr.toLowerCase().includes(q) || n.en.toLowerCase().includes(q) || n.ar.includes(query.trim());
    });
  }, [query, filter, state]);

  const countMemorized = Object.values(state.memorized).filter(Boolean).length;

  const speakName = (n: NameEntry) => {
    speakText(n.ar.replace(/[\u064B-\u0652]/g, ''), 'ar', 0.55);
  };

  return (
    <>
      {/* Bannière dorée — 99 Noms */}
      <PageBanner
        src="/images/banners/names.png"
        eyebrow="Asma-ul-Husna — أَسْمَاءُ اللَّهِ"
        title={lbl('Les 99 Noms d\'Allah', 'The 99 Names of Allah', 'أسماء الله الحسنى')}
      />
      <section id="section-noms" className="relative mx-auto max-w-7xl px-4 pb-20 pt-10 sm:px-6">
      <div className="text-center">
        <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
          {lbl('« Allah a 99 noms ; quiconque les compte entre au Paradis » — Bukhari, Muslim',
            '"Allah has 99 names; whoever counts them enters Paradise" — Bukhari, Muslim',
            '"لله تسعة وتسعون اسماً، من أحصاها دخل الجنة" — رواه البخاري ومسلم')}
        </p>

        {/* Progression */}
        <div className="mx-auto mt-6 flex max-w-md items-center gap-4">
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-black/40">
            <div className="h-full rounded-full bg-gradient-to-r from-accent to-amber-300 transition-all" style={{ width: `${(countMemorized / 99) * 100}%` }} />
          </div>
          <span className="text-sm font-bold text-accent">{countMemorized} / 99</span>
        </div>
      </div>

      {/* Recherche + filtres */}
      <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center gap-4 sm:flex-row">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={lbl('Rechercher un nom…', 'Search a name…', 'ابحث عن اسم…')}
            className="w-full rounded-full border border-accent/30 bg-emerald-950/40 py-2.5 pl-10 pr-4 text-foreground placeholder:text-muted-foreground/60 focus:border-accent focus:outline-none"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'memorized', 'favs'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                filter === f ? 'bg-accent text-emerald-950' : 'border border-accent/30 text-foreground hover:bg-accent/10'
              )}
            >
              {f === 'all' ? lbl('Tous', 'All', 'الكل') : f === 'memorized' ? lbl('Mémorisés', 'Memorized', 'محفوظة') : lbl('Favoris', 'Favourites', 'مفضلة')}
            </button>
          ))}
        </div>
      </div>

      {/* Grille des 99 noms */}
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {filtered.map((n) => (
          <div
            key={n.n}
            onClick={() => setSelected(n)}
            className={cn(
              'group relative cursor-pointer rounded-xl border p-4 text-center transition-all hover:border-accent/70 hover:shadow-[0_0_20px_rgba(212,175,55,0.12)]',
              state.memorized[n.n] ? 'border-emerald-500/50 bg-emerald-900/30' : 'border-accent/15 bg-gradient-to-br from-emerald-950/60 to-black/40'
            )}
          >
            {state.favs[n.n] && <Heart className="absolute top-2 ltr:right-2 rtl:left-2 h-3.5 w-3.5 fill-rose-500 text-rose-500" />}
            <span className="text-[10px] font-bold text-accent/60">#{n.n}</span>
            <p dir="rtl" lang="ar" className="mt-1 font-arabic text-3xl text-accent transition-transform group-hover:scale-105">{n.ar}</p>
            <p className="mt-2 text-sm font-semibold text-foreground">{n.tr}</p>
            <p className="text-xs text-muted-foreground">{uiLang === 'en' ? n.en : n.fr}</p>
            <button
              onClick={(e) => { e.stopPropagation(); speakName(n); }}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/25"
            >
              <Volume2 className="h-3 w-3" /> 🔊
            </button>
          </div>
        ))}
      </div>

      {/* Modale détail */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => { setSelected(null); stopSpeech(); }}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border-2 border-accent/50 bg-gradient-to-b from-emerald-950 to-black p-8 shadow-[0_0_60px_rgba(212,175,55,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setSelected(null)} className="absolute top-3 ltr:right-3 rtl:left-3 flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-accent hover:bg-accent/25">✕</button>
            <div className="text-center">
              <p className="text-xs font-bold text-accent/60">#{selected.n} / 99</p>
              <p dir="rtl" lang="ar" className="mt-3 font-arabic text-6xl text-accent">{selected.ar}</p>
              <p className="mt-4 font-display text-2xl font-bold text-foreground">{selected.tr}</p>
              <p className="text-lg text-muted-foreground">{uiLang === 'en' ? selected.en : selected.fr}</p>
              <button onClick={() => speakName(selected)} className="mt-4 flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-bold text-emerald-950 hover:bg-amber-300 mx-auto">
                <Volume2 className="h-4 w-4" /> {lbl('Écouter', 'Listen', 'استمع')}
              </button>
            </div>
            <div className="mt-6 rounded-xl border border-accent/20 bg-black/30 p-4">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent"><Sparkles className="h-3.5 w-3.5" /> {lbl('Explication', 'Explanation', 'الشرح')}</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground/85">{selected.exp}</p>
              {selected.ref && <p className="mt-3 text-xs font-semibold text-accent/80">📖 Coran {selected.ref}</p>}
            </div>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => persist({ ...state, memorized: { ...state.memorized, [selected.n]: !state.memorized[selected.n] } })}
                className={cn('flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold', state.memorized[selected.n] ? 'bg-emerald-500 text-white' : 'bg-accent/15 text-accent hover:bg-accent/30')}
              >
                {state.memorized[selected.n] ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                {state.memorized[selected.n] ? lbl('Mémorisé', 'Memorized', 'محفوظ') : lbl('Marquer mémorisé', 'Mark memorized', 'علّمه محفوظاً')}
              </button>
              <button
                onClick={() => persist({ ...state, favs: { ...state.favs, [selected.n]: !state.favs[selected.n] } })}
                className={cn('flex h-11 w-11 items-center justify-center rounded-full', state.favs[selected.n] ? 'bg-rose-500 text-white' : 'bg-accent/15 text-accent hover:bg-accent/30')}
              >
                <Heart className={cn('h-4 w-4', state.favs[selected.n] && 'fill-current')} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
    </>
  );
}
